import { MouseEventHandler, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { FaChevronRight as RightArrowIcon } from 'react-icons/fa'
import { TbAlertTriangle as WarningIcon } from 'react-icons/tb'
import { GoInfo as InfoIcon } from 'react-icons/go'
import { MessageType } from 'shop/types'
import { SimpleFormat } from 'shop/utils'
import { hideScrollbar } from '../common'
import { StyledHTMLElement } from 'shop/theme/types'
import { ellipsis } from '../Cart/commonStyles'

interface Props {
  title: string
  description: string
  messageType: MessageType
  isMessagesModal: boolean
  handleOpenModal?: MouseEventHandler<HTMLDivElement>
}

type MessageTheme = {
  border: string
  background: string
  text: string
}

const messageThemes = {
  ANNOUNCEMENT: {
    border: '#517845',
    background: '#F7FFF4',
    text: '#406935'
  },
  WARNING: {
    border: '#8E6D40',
    background: '#FFF5ED',
    text: '#785A37'
  }
}

export const Message = ({
  title,
  description,
  messageType,
  handleOpenModal = () => {},
  isMessagesModal = false
}: Props) => {
  const messageTheme = messageThemes[messageType]
  const [isTextTruncated, setIsTextTruncated] = useState(false)
  const descriptionRef = useRef<HTMLSpanElement>(null)

  const canShowMessagesModal = !isMessagesModal && isTextTruncated

  /** Determines whether to show CTA chevron based on whether there is text overflow */
  useEffect(() => {
    if (descriptionRef.current && !isMessagesModal) {
      const element = descriptionRef.current

      // Check if the content is overflowing (if multiple lines, scrollHeight should be greater than clientHeight)
      const isOverflowing = element.scrollHeight > element.clientHeight
      setIsTextTruncated(isOverflowing)
    }
  }, [description, isMessagesModal])

  return (
    <>
      <Container
        messageTheme={messageTheme}
        onClick={canShowMessagesModal ? handleOpenModal : () => {}}
        canShowMessagesModal={canShowMessagesModal}
      >
        <Content>
          <Title messageTheme={messageTheme}>
            {messageType === 'ANNOUNCEMENT' ? (
              <InfoIcon color={messageTheme.text} size={20} />
            ) : (
              <WarningIcon color={messageTheme.text} size={20} />
            )}
            <span>{title}</span>
            {canShowMessagesModal && <Chevron color={messageTheme.text} />}
          </Title>
          <Description
            ref={descriptionRef}
            messageTheme={messageTheme}
            shouldClampText={!isMessagesModal}
          >
            <SimpleFormat>{description}</SimpleFormat>
          </Description>
        </Content>
      </Container>
    </>
  )
}

export const Container = styled.div<
  StyledHTMLElement & {
    messageTheme: MessageTheme
    canShowMessagesModal: boolean
  }
>(({ theme, messageTheme, canShowMessagesModal }: any) => ({
  padding: '12px',
  display: 'block',
  overflowX: 'scroll',
  flex: '1 0 95%',
  border: `1px solid ${messageTheme.border}`,
  backgroundColor: messageTheme.background,
  boxShadow: '0px 4px 16px 0px #00000008',
  borderRadius: '8px',
  ...(hideScrollbar as any),
  [theme.mediaQueries.viewport7]: {
    flex: '0 1 50%',
    display: 'flex',
    gap: '12px',
    cursor: canShowMessagesModal ? 'pointer' : 'auto'
  }
}))

const Chevron = styled(RightArrowIcon)(() => ({
  fontSize: '12px',
  alignSelf: 'center'
}))

const Content = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'block',
  [theme.mediaQueries.viewport7]: {
    display: 'flex',
    flexFlow: 'column',
    width: '100%',
    gap: '6px'
  }
}))

const Title = styled.div<{ messageTheme: MessageTheme }>(
  ({ messageTheme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '18.75px',
    color: messageTheme.text,
    '> span': {
      marginRight: 'auto',
      paddingLeft: '10px'
    }
  })
)

const Description = styled.span<{
  shouldClampText: boolean
  messageTheme: MessageTheme
}>(({ shouldClampText, messageTheme }) => ({
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '16.41px',
  color: messageTheme.text,
  '& p': {
    margin: '0 0 6px 0'
  },

  ...(shouldClampText && {
    ...ellipsis,
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical'
  })
}))
